import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, VStack, SectionList } from 'native-base';
import moment from 'moment';
import { get, range } from 'lodash';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { formatToTimeOnly } from '@app/utils/date-formatter';
import { useSiteConfig, useSiteTime } from '@app/features/store-core';

type StoreHoursTableProps = {
  sections: { index: number; title: string; data: { title: string; dataFields: string[] }[] }[];
};

const StoreHoursTable = ({ sections }: StoreHoursTableProps) => {
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  const { siteConfig } = useSiteConfig();
  const siteTime = useSiteTime();
  const week = siteTime.toStartEndOfWeek();
  const [weekDates] = useState<Date[]>(range(7).map(day => moment(week.startDate).add(day, 'day').toDate()));

  const titlesWithQuestionMark = ['closed_non_op', 'dining_room', 'drive_thru_puw', 'open_twentyfour_hours', 'serves_breakfast'];

  return (
    <SectionList
      sections={sections}
      extraData={[locale, weekDates]}
      scrollEnabled
      showsVerticalScrollIndicator
      h="full"
      w="full"
      mt={0}
      px={4}
      renderSectionHeader={({ section }) => {
        return (
          <Box flex={1}>
            <HStack
              justifyContent="space-between"
              alignItems="center"
              pb={3}
              my={{ md: 3, xl: 5 }}
              space={2}
              borderBottomWidth={1}
              borderBottomColor="gray.300"
            >
              <Text size={{ md: 'md', xl: 'xl' }} color="black" fontWeight={600} lineHeight="sm">
                {t(`store_management:${section.title}`)}
              </Text>

              {/* Store Hours Info */}
              {section.index === 0 && (
                <HStack space={2} px={4} alignContent="flex-start" alignItems="center">
                  <Text color="gray.600" size="sm">
                    {t('store_management:first_day_of_the_week')}
                  </Text>
                  {typeof siteConfig.config?.storeHoursConfig?.firstDayOfWeek === 'number' && (
                    <Text key={locale} color="black" fontWeight={600} size="md">
                      {moment.weekdays(siteConfig.config?.storeHoursConfig?.firstDayOfWeek)}
                    </Text>
                  )}
                </HStack>
              )}
            </HStack>

            <HStack alignItems="center" height={{ md: '30px', xl: '40px' }}>
              <Box justifyContent="center" w={{ md: '15%', xl: '12%' }}></Box>
              <HStack flex={1} h="full" alignItems="center">
                {weekDates.map((day, i) => {
                  return (
                    <Box
                      key={`${section.title}[${day.toISOString()}]`}
                      flex={1}
                      h="full"
                      justifyContent="center"
                      bg={i % 2 === 0 ? 'gray.50' : 'white'}
                    >
                      <Text size={{ md: 'sm', xl: 'lg' }} fontWeight={600} textAlign="center" color="black">
                        {moment(day).format('dddd')}
                      </Text>
                    </Box>
                  );
                })}
              </HStack>
            </HStack>
          </Box>
        );
      }}
      renderItem={({ item }) => {
        return (
          <HStack alignItems="center" flex={1}>
            <Box justifyContent="center" w={{ md: '15%', xl: '12%' }}>
              <Text
                size={{ md: 'sm', xl: 'lg' }}
                fontWeight={500}
                color="black"
                lineHeight="sm"
                textTransform="capitalize"
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {t(`store_management:${item.title}`)}
                {titlesWithQuestionMark.includes(item.title) && '?'}
              </Text>
            </Box>

            <HStack flex={1}>
              {weekDates.map((day, i) => {
                const storeHours = siteTime.toOperationHours(day);
                return (
                  <HStack
                    key={`${item.title}[${day.toISOString()}]`}
                    flex={1}
                    height={{ md: '40px', xl: '50px' }}
                    bg={i % 2 === 0 ? 'gray.50' : 'white'}
                  >
                    {item.dataFields.map((field, index) => {
                      const value = get(storeHours, field);
                      const hasPickupWindow = get(storeHours, 'hasPickupWindow');
                      const hasDiningRoom = get(storeHours, 'hasDiningRoom');
                      const servesBreakfast = get(storeHours, 'iServesBreakfast');
                      return (
                        <Box
                          key={`${item.title}[${day.toISOString()}]][${field}}]`}
                          w={item.dataFields.length > 1 ? '1/2' : 'full'}
                          justifyContent="center"
                          borderColor="gray.300"
                          borderTopWidth={1}
                          alignItems="center"
                          py={2}
                        >
                          {typeof value === 'boolean' && (
                            <Text textAlign="center" fontWeight={600} color="black" size={{ md: 'sm', xl: 'md' }}>
                              {value === true ? t('store_management:yes') : t('store_management:no')}
                            </Text>
                          )}
                          {typeof value === 'object' && (
                            <VStack justifyContent="center" alignItems="flex-start">
                              <Text textAlign="left" color="gray.600" fontWeight={500} size={{ md: 'xs', xl: 'md' }} lineHeight="sm">
                                {index === 0 ? t('store_management:open') : t('store_management:close')}
                              </Text>
                              <Text textAlign="left" lineHeight="sm" fontWeight={600} color="black" size={{ md: 'xs', xl: 'md' }}>
                                {(!hasDiningRoom && item.title === 'dining_room_hours') ||
                                (!hasPickupWindow && item.title === 'drive_thru_hours') ||
                                (!servesBreakfast && item.title === 'breakfast_hours') ||
                                !value
                                  ? '--'
                                  : formatToTimeOnly(value)}
                              </Text>

                              {/* TODO: will enable: waiting for further instruction */}
                              {/* {index === 1 && (
                                <MaterialCommunityIcons
                                  name="clock-plus-outline"
                                  size={15}
                                  color={colors.blue[500]}
                                  style={{ position: 'absolute', right: -18, bottom: 5 }}
                                />
                              )} */}
                            </VStack>
                          )}
                        </Box>
                      );
                    })}
                  </HStack>
                );
              })}
            </HStack>
          </HStack>
        );
      }}
    />
  );
};

export default StoreHoursTable;
